import { DealFields } from '../../../state_manager/data_models/deal_record.ts'
import { DealCommitmentEntryFields } from '../../../state_manager/data_models/deal_commitment_entry.ts'

/**
 * Commit gate — what "committing a deal" means in cfg terms, and which details
 * a committed deal is expected to carry.
 *
 * A deal is committed when it moves into an origination stage whose pipeline
 * status carries the `won` outcome (cfg.pipeline_outcomes, `deal_origination`
 * kind). Nothing here matches a stage NAME — rename "Committed" in
 * Configuration and the gate keeps working; only the outcome key is contractual
 * (the same rule the Bulk Add Prospects page follows for `invested`).
 *
 * The details are the ones the Committed stage action drafts transactions
 * from — our commitment, the security type issued, a valuation (pre- or
 * post-money, either satisfies the check) — plus the company's industry
 * (owner instruction 2026-09-16).
 *
 * The Critical Technology Area joins them CONDITIONALLY: it is an SBA
 * reporting dimension, so it is asked for only when the money could come from
 * an SBIC fund — either no commitment has been entered yet (the fund is still
 * unknown, so the question may yet apply), or at least one commitment row
 * names a fund with is_sbic. Committing into a non-SBIC fund never raises it.
 * Like every other gap it PROMPTS and never blocks: an SBIC fund may hold
 * investments that fall under no CTA at all.
 */
export const OUTCOME_KEY_WON = 'won'
export const PIPELINE_TYPE_KEY_DEAL_ORIGINATION = 'deal_origination'

export interface CommitGateCfg {
  pipeline_configs?: Array<{ id: string; stage?: string | null }> | null
  pipeline_stages?: Array<{ id: string; decision_status?: string | null }> | null
  pipeline_statuses?: Array<{ id: string; pipeline_type_key: string; outcome_key?: string | null }> | null
  /** globalConfigs.funds — only is_sbic is read. */
  funds?: Array<{ id: string; is_sbic?: boolean | null }> | null
}

/** True when `configId` is an origination stage whose status outcome is Won. */
export function isOriginationCommitTarget(configId: string | null | undefined, cfg: CommitGateCfg): boolean {
  if (!configId) return false
  const config = (cfg.pipeline_configs || []).find((c) => c.id === configId)
  if (!config?.stage) return false
  const stage = (cfg.pipeline_stages || []).find((s) => s.id === config.stage)
  if (!stage?.decision_status) return false
  const status = (cfg.pipeline_statuses || []).find((s) => s.id === stage.decision_status)
  return (
    !!status &&
    status.pipeline_type_key === PIPELINE_TYPE_KEY_DEAL_ORIGINATION &&
    status.outcome_key === OUTCOME_KEY_WON
  )
}

/** Each flag is true when that detail is MISSING. */
export interface CommitGaps {
  commitment: boolean
  securityType: boolean
  valuation: boolean
  industry: boolean
  /** Missing AND relevant — false when the deal commits into a non-SBIC fund,
   *  so a non-SBIC commitment never shows the row at all. */
  criticalTechnologyArea: boolean
}

const positive = (value: unknown): boolean => {
  const n = typeof value === 'string' ? Number(value) : value
  return typeof n === 'number' && Number.isFinite(n) && n > 0
}

/**
 * Reads the deal record shape (`DealFields`). The New Deal drawer keeps its
 * commitments outside the form data and only holds the chosen company's id,
 * so it hands the commitment total and the company's industry in via `extra`.
 */
export function findCommitGaps(
  record: Record<string, any> | null | undefined,
  extra?: {
    commitmentAmount?: number | null
    industry?: string | null
    criticalTechnologyArea?: string | null
    /** The fund(s) the drawer's un-saved commitment rows point at. */
    fundIds?: string[] | null
  },
  cfg?: CommitGateCfg
): CommitGaps {
  const r = record || {}
  const rows = (r[DealFields.prog__deal_commitments] || []) as Array<Record<string, unknown>>
  const rowTotal = rows.reduce((sum, row) => {
    const amount = row[DealCommitmentEntryFields.deal_commitments__amount]
    return sum + (positive(amount) ? Number(amount) : 0)
  }, 0)
  const commitmentAmount =
    extra?.commitmentAmount !== undefined && extra?.commitmentAmount !== null
      ? extra.commitmentAmount
      : positive(r[DealFields.prog__commitment_amount])
        ? Number(r[DealFields.prog__commitment_amount])
        : rowTotal
  const industry = extra?.industry !== undefined ? extra.industry : r[DealFields.accounts__industry]
  const cta =
    extra?.criticalTechnologyArea !== undefined
      ? extra.criticalTechnologyArea
      : r[DealFields.accounts__critical_technology_area]

  // Which funds is this deal committing into? The drawer hands its un-saved
  // rows in via extra.fundIds; everywhere else they are on the record.
  const fundIds =
    extra?.fundIds !== undefined && extra?.fundIds !== null
      ? extra.fundIds.filter(Boolean)
      : rows
          .map((row) => row[DealCommitmentEntryFields.deal_commitments__fund_id] as string | undefined)
          .filter((id): id is string => !!id)
  const sbicIds = new Set(
    (cfg?.funds || []).filter((fund) => fund?.is_sbic && fund.id).map((fund) => fund.id)
  )
  // No commitment yet => the fund is unknown, so the question may still apply
  // and we ask. With commitments, ask only if one of them is an SBIC fund.
  const sbicRelevant = fundIds.length === 0 || fundIds.some((id) => sbicIds.has(id))

  return {
    commitment: !positive(commitmentAmount),
    securityType: !r[DealFields.rounds__security_type],
    valuation:
      !positive(r[DealFields.rounds__pre_money_valuation]) && !positive(r[DealFields.rounds__post_money_valuation]),
    industry: !industry,
    criticalTechnologyArea: !cta && sbicRelevant
  }
}

export const hasCommitGaps = (gaps: CommitGaps): boolean =>
  gaps.commitment || gaps.securityType || gaps.valuation || gaps.industry || gaps.criticalTechnologyArea
